/*
Hook for video upload functionality for ChileAutos integration.
Handles file upload with progress tracking and validation.
Autor: Assistant
Fecha: 2025-01-02
*/
import { useState } from 'react'

import { getToken } from '../utils/utils'

import { makeUrlVideoCrea, makeUrlVideoDelete } from './connectionData'
import { useAuth } from './useAuth'

interface VideoUploadParams {
  vehicleId: string
  file: File
  duration: number
}

interface VideoDeleteParams {
  vehicleId: string
}

interface VideoUploadResponse {
  success: boolean
  videoId: string
  message: string
}

const useVideoUpload = () => {
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const { userData } = useAuth()

  const uploadVideo = async (params: VideoUploadParams): Promise<VideoUploadResponse | null> => {
    if (!userData?.profile) {
      setUploadError('Usuario no autenticado')

      return null
    }

    setIsUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('video', params.file)
      formData.append('vehicle_id', params.vehicleId)
      formData.append('organization_id', userData.organization?._id ?? '')
      formData.append('user_id', userData.profile._id)
      formData.append('duration', params.duration.toString())

      const apiUrl = makeUrlVideoCrea()

      const token = getToken()
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Error al subir el video')
      }

      const result: VideoUploadResponse = await response.json()
      setUploadProgress(100)

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
      setUploadError(errorMessage)

      return null
    } finally {
      setIsUploading(false)
    }
  }

  const deleteVideo = async (params: VideoDeleteParams): Promise<boolean> => {
    if (!userData?.profile) {
      setDeleteError('Usuario no autenticado')

      return false
    }

    setIsDeleting(true)
    setDeleteError(null)

    try {
      const apiUrl = makeUrlVideoDelete()

      const token = getToken()
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_id: params.vehicleId,
          user_id: userData.profile._id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Error al eliminar el video')
      }

      return true
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
      setDeleteError(errorMessage)

      return false
    } finally {
      setIsDeleting(false)
    }
  }

  // Validate video file
  const validateVideoFile = (file: File): { isValid: boolean; error?: string } => {
    // Check file type
    const allowedTypes = ['video/mp4', 'video/quicktime']

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Solo se permiten archivos MP4 y MOV' }
    }

    // Check file size (100MB)
    const maxSize = 100 * 1024 * 1024

    if (file.size > maxSize) {
      return { isValid: false, error: 'El archivo es demasiado grande. Máximo 100MB' }
    }

    return { isValid: true }
  }

  // Get video duration from file
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src)
        resolve(video.duration)
      }

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src)
        reject(new Error('Error al leer el archivo de video'))
      }

      video.src = URL.createObjectURL(file)
    })
  }

  return {
    uploadVideo,
    deleteVideo,
    validateVideoFile,
    getVideoDuration,
    uploadProgress,
    isUploading,
    uploadError,
    deleteError,
    isDeleting,
  }
}

export default useVideoUpload
